/**
 * DCS ATS — public candidate page at /cv/:token
 * =============================================================================
 *
 * REPLACES the existing frontend/src/pages/ClientCVViewerPage.js.
 * Same route, same props, same decision API — only the shell and the way the
 * documents are embedded change.
 *
 * WHAT WAS WRONG WITH THE PAGE THIS REPLACES
 * ------------------------------------------
 * 1. It opened with
 *        <div className="min-h-screen bg-gradient-to-br from-slate-900
 *                        via-slate-800 to-slate-900">
 *    and built every card from `bg-white/5 backdrop-blur` with emerald buttons.
 *    That is the "totally black background, barely visible" the owner reported,
 *    and it shares nothing with the cream-and-gold email the link arrives in.
 *    A corporate HR manager in India opens that link expecting the firm that
 *    wrote to them; they got a dark developer dashboard.
 *
 * 2. Both tabs were PDF iframes:
 *        <iframe src={`${API}/api/public/cv/${token}/pdf?part=dossier`} />
 *        <iframe src={`${API}/api/public/cv/${token}/pdf?part=cv`} />
 *    server.py falls back to the COMBINED pdf when a per-part file is missing,
 *    so both tabs served the same document. That is the "dossier and resume are
 *    the same" complaint, and it was in the backend, not here.
 *
 * 3. A PDF in an iframe is close to unusable on a phone — fixed page width,
 *    pinch to zoom, its own scrollbar inside the page's scrollbar. Most Indian
 *    hiring managers will open this link on a phone.
 *
 * WHAT THIS DOES INSTEAD
 * ----------------------
 * * DCS palette: cream #faf7f0, white cards, #e5e1d5 borders, gold #c9a84c,
 *   Outfit — the same values as the approved agreement email, so the link and
 *   the mail that carried it are visibly one firm.
 * * Each document is real, reflowing HTML delivered by the new
 *   /api/public/cv/:token/v2 endpoint and rendered in its own <iframe srcDoc>.
 *   The iframe is for STYLE ISOLATION, not for PDF: the two documents carry
 *   their own stylesheets and would otherwise fight each other and the host
 *   app's theme (which uses oklch() and breaks html2canvas). Being same-origin,
 *   the parent measures the content and sizes the frame, so there is no inner
 *   scrollbar and the whole thing reflows on a phone.
 * * The two tabs come from two different strings in the response. There is no
 *   code path that can put the same document in both.
 * * NO design-system imports. A public client page must not depend on the ATS's
 *   theme, or a future theme change silently redecorates a client's document.
 *
 * The decision API is untouched: POST /api/public/cv/:token/decision with the
 * same FormData fields (decision, notes, interview_date, interview_time,
 * interview_mode), so approvals keep landing where the recruiters already look.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/* The one place these values live on the frontend. Keep in step with
   backend/routes/dossier_v2.py :: PALETTE and the mail templates. */
const C = {
  cream: '#faf7f0',
  card: '#ffffff',
  border: '#e5e1d5',
  ink: '#2b2b2b',
  muted: '#7a7a7a',
  gold: '#c9a84c',
  goldDark: '#b08d36',
  goldLight: '#d4b96a',
  navy: '#2e3a68',
};

const FONT =
  "'Outfit', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, " +
  "'Helvetica Neue', Arial, sans-serif";

/* Scoped to .dcs-cv so nothing here can leak into the rest of the ATS if this
   component is ever rendered inside it. */
const PAGE_CSS = `
.dcs-cv{background:${C.cream};color:${C.ink};font-family:${FONT};font-size:15px;
 line-height:1.6;min-height:100vh;-webkit-font-smoothing:antialiased}
.dcs-cv *,.dcs-cv *::before,.dcs-cv *::after{box-sizing:border-box}
.dcs-cv a{color:${C.goldDark}}
.dcs-topbar{position:sticky;top:0;z-index:20;background:rgba(255,255,255,.93);
 backdrop-filter:saturate(150%) blur(8px);border-bottom:1px solid ${C.border}}
.dcs-topbar-in{max-width:960px;margin:0 auto;padding:12px 18px;display:flex;
 align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap}
.dcs-brand{display:flex;align-items:center;gap:10px;min-width:0}
.dcs-mark{width:30px;height:30px;border-radius:8px;flex:0 0 auto;color:#fff;
 background:linear-gradient(135deg,${C.goldLight},${C.gold},${C.goldDark});
 display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px}
.dcs-brand-name{font-weight:600;font-size:15px;line-height:1.2}
.dcs-brand-sub{color:${C.muted};font-size:11.5px;letter-spacing:.05em;
 text-transform:uppercase}
.dcs-pill{display:inline-flex;align-items:center;gap:7px;border:1px solid ${C.border};
 background:${C.cream};border-radius:999px;padding:6px 14px;font-size:12.5px;
 color:${C.muted};white-space:nowrap}
.dcs-pill b{color:${C.ink};font-weight:600}
.dcs-pill.warn{border-color:#e6c98a;background:#fdf6e6;color:#8a6d24}
.dcs-pill.dead{border-color:#e2c9c9;background:#fbf0f0;color:#8f4a4a}
.dcs-pill.done{border-color:#cfe0cd;background:#f2f8f1;color:#3f6b3c}
.dcs-wrap{max-width:960px;margin:0 auto;padding:22px 18px 64px}
.dcs-card{background:${C.card};border:1px solid ${C.border};border-radius:18px;
 overflow:hidden;box-shadow:0 16px 40px -34px rgba(176,141,54,.5)}
.dcs-goldbar{height:5px;background:linear-gradient(90deg,${C.goldLight},${C.gold},${C.goldDark})}
.dcs-head{padding:26px 30px}
.dcs-kicker{color:${C.goldDark};font-size:11px;font-weight:700;letter-spacing:.14em;
 text-transform:uppercase;margin:0 0 9px}
.dcs-h1{font-size:26px;font-weight:600;letter-spacing:-.01em;line-height:1.25;margin:0 0 4px}
.dcs-ref{color:${C.muted};font-size:15px;margin:0}
.dcs-facts{display:flex;flex-wrap:wrap;gap:7px 18px;margin:14px 0 0;color:${C.muted};
 font-size:14px}
.dcs-facts b{color:${C.ink};font-weight:600}
.dcs-headrow{display:flex;justify-content:space-between;gap:22px;flex-wrap:wrap;
 align-items:flex-start}
.dcs-actions{display:flex;gap:10px;flex-wrap:wrap}
.dcs-btn{appearance:none;border:0;cursor:pointer;font-family:inherit;font-size:14.5px;
 font-weight:600;padding:12px 22px;border-radius:12px;line-height:1.2;
 text-decoration:none;display:inline-flex;align-items:center;justify-content:center;gap:8px}
.dcs-btn[disabled]{opacity:.55;cursor:default}
.dcs-btn-gold{background:linear-gradient(135deg,${C.goldLight},${C.gold},${C.goldDark});
 color:#fff;box-shadow:0 8px 18px -10px rgba(176,141,54,.8)}
.dcs-btn-ghost{background:${C.card};color:${C.ink};border:1px solid ${C.border}}
.dcs-btn-quiet{background:transparent;color:${C.muted};border:1px solid ${C.border};
 font-weight:500;font-size:13px;padding:8px 14px;border-radius:10px}
.dcs-btn:focus-visible{outline:2px solid ${C.navy};outline-offset:2px}
.dcs-idnote{display:flex;gap:9px;align-items:flex-start;margin:16px 0 0;padding:11px 14px;
 background:${C.cream};border:1px solid ${C.border};border-radius:12px;color:${C.muted};
 font-size:13px}
.dcs-tabs{display:flex;gap:6px;margin:26px 0 0;padding:5px;background:${C.cream};
 border:1px solid ${C.border};border-radius:14px;width:fit-content;max-width:100%;
 flex-wrap:wrap}
.dcs-tab{appearance:none;border:0;background:transparent;font-family:inherit;font-size:14px;
 font-weight:600;color:${C.muted};padding:10px 20px;border-radius:10px;cursor:pointer;
 white-space:nowrap}
.dcs-tab[aria-selected="true"]{background:${C.card};color:${C.goldDark};
 box-shadow:0 2px 8px -4px rgba(0,0,0,.2)}
.dcs-doctools{display:flex;justify-content:space-between;align-items:center;gap:12px;
 padding:12px 18px;border:1px solid ${C.border};border-bottom:0;
 border-radius:14px 14px 0 0;background:${C.cream};flex-wrap:wrap;margin-top:16px}
.dcs-doctitle{font-size:12px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;
 color:${C.muted}}
.dcs-frame{display:block;width:100%;height:1100px;border:1px solid ${C.border};
 border-radius:0 0 14px 14px;background:${C.card}}
.dcs-foot{margin-top:30px;text-align:center;color:${C.muted};font-size:12.5px;line-height:1.7}
.dcs-foot b{color:${C.ink}}
.dcs-msg{max-width:560px;margin:12vh auto;padding:32px;text-align:center;
 background:${C.card};border:1px solid ${C.border};border-radius:18px}
.dcs-msg h2{margin:0 0 8px;font-size:20px;font-weight:600}
.dcs-msg p{margin:0;color:${C.muted}}
.dcs-modal-bg{position:fixed;inset:0;background:rgba(30,26,18,.45);z-index:60;
 display:flex;align-items:center;justify-content:center;padding:18px}
.dcs-modal{background:${C.card};border-radius:18px;max-width:520px;width:100%;
 max-height:90vh;overflow:auto;border:1px solid ${C.border}}
.dcs-modal-in{padding:26px 28px}
.dcs-modal h3{margin:0 0 6px;font-size:19px;font-weight:600}
.dcs-modal p.sub{margin:0 0 18px;color:${C.muted};font-size:14px}
.dcs-label{display:block;font-size:12px;font-weight:700;letter-spacing:.07em;
 text-transform:uppercase;color:${C.muted};margin:14px 0 6px}
.dcs-input,.dcs-textarea,.dcs-select{width:100%;font-family:inherit;font-size:15px;
 color:${C.ink};background:${C.card};border:1px solid ${C.border};border-radius:10px;
 padding:10px 12px}
.dcs-textarea{min-height:92px;resize:vertical}
.dcs-row2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.dcs-modal-foot{display:flex;gap:10px;justify-content:flex-end;margin-top:22px;flex-wrap:wrap}
.dcs-err{color:#8f4a4a;font-size:13.5px;margin-top:10px}
@media (max-width:700px){
 .dcs-head{padding:22px 18px}
 .dcs-h1{font-size:22px}
 .dcs-wrap{padding:14px 12px calc(44px + env(safe-area-inset-bottom,0px))}
 .dcs-actions{width:100%}
 .dcs-actions .dcs-btn{flex:1 1 46%}
 .dcs-tabs{width:100%}
 .dcs-tab{flex:1 1 0;text-align:center;padding:10px 8px;min-height:44px}
 .dcs-frame{height:800px}
 .dcs-row2{grid-template-columns:1fr}
 .dcs-btn{min-height:48px}
 .dcs-btn-quiet{min-height:44px}
 /* 16px, or iOS Safari zooms the layout viewport the moment a field is focused */
 .dcs-input,.dcs-textarea,.dcs-select{font-size:16px;min-height:46px}
 .dcs-modal-bg{padding:12px;align-items:flex-end}
 .dcs-modal{max-height:88vh}
 .dcs-modal-in{padding:20px 18px calc(20px + env(safe-area-inset-bottom,0px))}
 .dcs-modal-foot .dcs-btn{flex:1 1 46%}
 .dcs-topbar-in{padding:10px 12px}
}
@media print{
 .dcs-topbar,.dcs-actions,.dcs-tabs,.dcs-doctools,.dcs-foot,.dcs-idnote{display:none!important}
 .dcs-cv{background:#fff}
 .dcs-wrap{padding:0;max-width:none}
 .dcs-card{border:0;border-radius:0;box-shadow:none}
 .dcs-frame{border:0;height:auto}
 @page{size:A4;margin:12mm}
}
`;

/* --------------------------------------------------------------------------
 * A document in its own iframe, sized to its content.
 * ------------------------------------------------------------------------ */

function DocumentFrame({ html, title, frameRef }) {
  const localRef = useRef(null);
  const ref = frameRef || localRef;

  /* srcDoc is same-origin, so the parent can read the inner scrollHeight and
     remove the inner scrollbar. Fonts land after load and change the height, so
     it is measured again twice — cheap, and the alternative is a document that
     is cut off at the fold on a phone. */
  const fit = useCallback(() => {
    const frame = ref.current;
    if (!frame) return;
    try {
      const doc = frame.contentDocument;
      if (!doc || !doc.body) return;
      const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
      if (h > 0) frame.style.height = `${h + 24}px`;
    } catch (e) {
      /* Sandboxed or not ready — the CSS fallback height keeps it readable. */
    }
  }, [ref]);

  useEffect(() => {
    fit();
    const t1 = setTimeout(fit, 300);
    const t2 = setTimeout(fit, 1200);
    window.addEventListener('resize', fit);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', fit);
    };
  }, [html, fit]);

  return (
    <iframe
      ref={ref}
      className="dcs-frame"
      title={title}
      onLoad={fit}
      /* allow-same-origin so the height can be measured; no allow-scripts, so
         a document can never run code in this page's origin. */
      sandbox="allow-same-origin allow-modals allow-popups"
      srcDoc={html}
    />
  );
}

/* -------------------------------------------------------------------------- */

function useCountdown(expiresAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return useMemo(() => {
    if (!expiresAt) return null;
    const end = new Date(expiresAt).getTime();
    if (Number.isNaN(end)) return null;
    const diff = end - now;
    if (diff <= 0) return { expired: true, label: null };
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    if (days >= 1) {
      return { expired: false, label: `in ${days} day${days === 1 ? '' : 's'}` };
    }
    const mins = Math.floor((diff % 3600000) / 60000);
    return { expired: false, label: hours >= 1 ? `in ${hours}h ${mins}m` : `in ${mins}m` };
  }, [expiresAt, now]);
}

/* -------------------------------------------------------------------------- */

export default function ClientCVViewerPage() {
  const { token } = useParams();

  const [data, setData] = useState(null);
  const [legacy, setLegacy] = useState(null); // set only if /v2 is unavailable
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('dossier');
  const [modal, setModal] = useState(null); // 'approve' | 'decline' | null
  const [decision, setDecision] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);

  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('10:00');
  const [interviewMode, setInterviewMode] = useState('video');

  const dossierFrame = useRef(null);
  const resumeFrame = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch(`${API_URL}/api/public/cv/${token}/v2`);
        if (res.ok) {
          const body = await res.json();
          if (!cancelled) {
            setData(body);
            setDecision(body.decision || null);
            setLoading(false);
          }
          return;
        }
        /* 404 and 410 are real answers about the LINK (not valid / withdrawn /
           expired) and must be shown as such, not retried against the old
           route. Anything else means v2 is not deployed or could not build the
           document, and the legacy payload still serves the client. */
        if (res.status === 404 || res.status === 410) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) {
            setError(body.detail || 'This link is no longer available.');
            setLoading(false);
          }
          return;
        }
        throw new Error(`v2 unavailable (${res.status})`);
      } catch (e) {
        /* Fall back to the endpoint the old page used, so a client is never
           left staring at an error because a deploy is half-done. The chrome
           stays the new one; only the documents come from the old PDFs. */
        try {
          const res = await fetch(`${API_URL}/api/public/cv/${token}`);
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.detail || 'This link is no longer available.');
          }
          const body = await res.json();
          if (!cancelled) {
            setLegacy(body.dossier || {});
            setDecision((body.dossier || {}).decision || null);
            setLoading(false);
          }
        } catch (e2) {
          if (!cancelled) {
            setError(e2.message || 'We could not open this profile.');
            setLoading(false);
          }
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const expiresAt = data?.expires_at || legacy?.expires_at || null;
  const countdown = useCountdown(expiresAt);

  const submit = useCallback(
    async (kind) => {
      if (kind === 'approved' && !interviewDate) {
        setFormError('Please choose a date that suits you.');
        return;
      }
      if (kind === 'rejected' && !reason.trim()) {
        setFormError('A one-line reason helps us send you better people next time.');
        return;
      }
      setBusy(true);
      setFormError(null);
      try {
        const form = new FormData();
        form.append('decision', kind);
        form.append('notes', kind === 'approved' ? notes : reason);
        if (kind === 'approved') {
          form.append('interview_date', interviewDate);
          form.append('interview_time', interviewTime || '10:00');
          form.append('interview_mode', interviewMode);
        }
        const res = await fetch(`${API_URL}/api/public/cv/${token}/decision`, {
          method: 'POST',
          body: form,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || 'We could not record that. Please try again.');
        }
        setDecision(kind === 'approved' ? 'approved' : 'rejected');
        setModal(null);
      } catch (e) {
        setFormError(e.message);
      } finally {
        setBusy(false);
      }
    },
    [token, notes, reason, interviewDate, interviewTime, interviewMode],
  );

  /* Printing must print the DOCUMENT, not the viewer chrome around it. */
  const printDoc = useCallback(() => {
    const frame = tab === 'dossier' ? dossierFrame.current : resumeFrame.current;
    if (frame && frame.contentWindow) {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } else {
      window.print();
    }
  }, [tab]);

  if (loading) {
    return (
      <div className="dcs-cv">
        <style>{PAGE_CSS}</style>
        <div className="dcs-msg">
          <h2>Opening the profile…</h2>
          <p>One moment.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dcs-cv">
        <style>{PAGE_CSS}</style>
        <div className="dcs-msg">
          <h2>This link is not available</h2>
          <p>{error}</p>
          <p style={{ marginTop: 12 }}>
            Please contact your consultant at{' '}
            <a href="mailto:care@daruwallas.com">care@daruwallas.com</a>.
          </p>
        </div>
      </div>
    );
  }

  const label = data?.candidate_label || 'Confidential candidate';
  const position = data?.position || legacy?.requirement_snapshot?.position_title || '';
  const reference = data?.reference || legacy?.dossier_ref || '';
  const snap = legacy?.candidate_snapshot || {};

  /* Only ever facts the server chose to send. Nothing is derived here — a
     frontend that computes a candidate fact is a frontend that can get it
     wrong in a way nobody can trace. */
  const facts = [];
  if (!data && snap.current_designation) facts.push(['Current role', snap.current_designation]);
  if (!data && snap.total_experience_years) {
    facts.push(['Experience', `${snap.total_experience_years} years`]);
  }
  if (!data && snap.location) facts.push(['Based in', snap.location]);

  return (
    <div className="dcs-cv">
      <style>{PAGE_CSS}</style>

      <header className="dcs-topbar">
        <div className="dcs-topbar-in">
          <div className="dcs-brand">
            <div className="dcs-mark">DCS</div>
            <div>
              <div className="dcs-brand-name">Daruwalla Consultancy Services</div>
              <div className="dcs-brand-sub">Confidential candidate profile</div>
            </div>
          </div>
          {decision ? (
            <span className="dcs-pill done">
              <b>{decision === 'approved' ? 'Shortlisted' : 'Declined'}</b> — your
              consultant has been notified
            </span>
          ) : countdown?.expired ? (
            <span className="dcs-pill dead">This link has expired</span>
          ) : countdown?.label ? (
            <span className="dcs-pill warn">
              Access closes <b>{countdown.label}</b>
            </span>
          ) : null}
        </div>
      </header>

      <div className="dcs-wrap">
        <div className="dcs-card">
          <div className="dcs-goldbar" />
          <div className="dcs-head">
            <div className="dcs-headrow">
              <div style={{ minWidth: 0, flex: '1 1 320px' }}>
                <div className="dcs-kicker">
                  {position ? `Submitted for ${position}` : 'Candidate profile'}
                </div>
                <h1 className="dcs-h1" data-testid="candidate-name">
                  {label}
                </h1>
                {reference ? <p className="dcs-ref">Reference {reference}</p> : null}
                {facts.length > 0 ? (
                  <div className="dcs-facts">
                    {facts.map(([k, v]) => (
                      <span key={k}>
                        {k} <b>{v}</b>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              {!decision && !countdown?.expired ? (
                <div className="dcs-actions">
                  <button
                    type="button"
                    className="dcs-btn dcs-btn-gold"
                    onClick={() => {
                      setFormError(null);
                      setModal('approve');
                    }}
                    data-testid="approve-btn"
                  >
                    Shortlist for interview
                  </button>
                  <button
                    type="button"
                    className="dcs-btn dcs-btn-ghost"
                    onClick={() => {
                      setFormError(null);
                      setModal('decline');
                    }}
                    data-testid="reject-btn"
                  >
                    Not for this role
                  </button>
                </div>
              ) : null}
            </div>

            {data?.identity_protected !== false ? (
              <div className="dcs-idnote" data-testid="identity-protected-note">
                <span aria-hidden="true">🔒</span>
                <span>
                  The candidate’s name, employers and contact details are withheld at
                  this stage. They are released to you as soon as you shortlist.
                </span>
              </div>
            ) : null}

            <div className="dcs-tabs" role="tablist" aria-label="Documents">
              <button
                type="button"
                role="tab"
                className="dcs-tab"
                aria-selected={tab === 'dossier'}
                onClick={() => setTab('dossier')}
              >
                Our assessment
              </button>
              <button
                type="button"
                role="tab"
                className="dcs-tab"
                aria-selected={tab === 'resume'}
                onClick={() => setTab('resume')}
              >
                Redacted CV
              </button>
            </div>

            <div className="dcs-doctools">
              <span className="dcs-doctitle">
                {tab === 'dossier'
                  ? 'Daruwalla Consultancy Services — candidate assessment'
                  : 'Candidate CV — identifying details removed'}
              </span>
              <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="dcs-btn dcs-btn-quiet" onClick={printDoc}>
                  Print / save as PDF
                </button>
                <a
                  className="dcs-btn dcs-btn-quiet"
                  href={
                    data
                      ? `${API_URL}/api/public/cv/${token}/document/${tab}`
                      : `${API_URL}/api/public/cv/${token}/pdf?part=${
                          tab === 'dossier' ? 'dossier' : 'cv'
                        }`
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in a new tab
                </a>
              </span>
            </div>

            {/* Two documents, two sources. Kept mounted so switching tabs does
                not re-measure and jump; only one is visible. */}
            <div hidden={tab !== 'dossier'}>
              {data ? (
                <DocumentFrame
                  frameRef={dossierFrame}
                  html={data.documents.dossier}
                  title="Candidate assessment"
                />
              ) : (
                <iframe
                  ref={dossierFrame}
                  className="dcs-frame"
                  title="Candidate assessment"
                  src={`${API_URL}/api/public/cv/${token}/pdf?part=dossier`}
                />
              )}
            </div>
            <div hidden={tab !== 'resume'}>
              {data ? (
                <DocumentFrame
                  frameRef={resumeFrame}
                  html={data.documents.resume}
                  title="Redacted CV"
                />
              ) : (
                <iframe
                  ref={resumeFrame}
                  className="dcs-frame"
                  title="Redacted CV"
                  src={`${API_URL}/api/public/cv/${token}/pdf?part=cv`}
                />
              )}
            </div>
          </div>
        </div>

        <div className="dcs-foot">
          <b>Daruwalla Consultancy Services</b> · 29 Hanuman Building, Behind Old RBI,
          Fort, Mumbai 400001
          <br />
          <a href="mailto:care@daruwallas.com">care@daruwallas.com</a> ·{' '}
          <a href="https://daruwallas.com">daruwallas.com</a>
          <br />
          Shared in confidence for this vacancy only. Please do not forward outside your
          hiring team.
        </div>
      </div>

      {modal ? (
        <div
          className="dcs-modal-bg"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) setModal(null);
          }}
        >
          <div className="dcs-modal">
            <div className="dcs-goldbar" />
            <div className="dcs-modal-in">
              {modal === 'approve' ? (
                <>
                  <h3>Shortlist {label}</h3>
                  <p className="sub">
                    Tell us when suits you. Your consultant confirms the final time with
                    the candidate and comes back to you.
                  </p>
                  <label className="dcs-label" htmlFor="dcs-date">
                    Preferred date
                  </label>
                  <div className="dcs-row2">
                    <input
                      id="dcs-date"
                      className="dcs-input"
                      type="date"
                      value={interviewDate}
                      onChange={(e) => setInterviewDate(e.target.value)}
                    />
                    <input
                      className="dcs-input"
                      type="time"
                      value={interviewTime}
                      onChange={(e) => setInterviewTime(e.target.value)}
                    />
                  </div>
                  <label className="dcs-label" htmlFor="dcs-mode">
                    Format
                  </label>
                  <select
                    id="dcs-mode"
                    className="dcs-select"
                    value={interviewMode}
                    onChange={(e) => setInterviewMode(e.target.value)}
                  >
                    <option value="video">Video call</option>
                    <option value="phone">Telephone</option>
                    <option value="in_person">In person</option>
                  </select>
                  <label className="dcs-label" htmlFor="dcs-notes">
                    Anything we should brief the candidate on (optional)
                  </label>
                  <textarea
                    id="dcs-notes"
                    className="dcs-textarea"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </>
              ) : (
                <>
                  <h3>Not the right fit</h3>
                  <p className="sub">
                    One line is enough. It is the fastest way to get you a better
                    shortlist.
                  </p>
                  <label className="dcs-label" htmlFor="dcs-reason">
                    What was missing?
                  </label>
                  <textarea
                    id="dcs-reason"
                    className="dcs-textarea"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </>
              )}

              {formError ? <div className="dcs-err">{formError}</div> : null}

              <div className="dcs-modal-foot">
                <button
                  type="button"
                  className="dcs-btn dcs-btn-ghost"
                  onClick={() => setModal(null)}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="dcs-btn dcs-btn-gold"
                  disabled={busy}
                  onClick={() => submit(modal === 'approve' ? 'approved' : 'rejected')}
                >
                  {busy ? 'Sending…' : modal === 'approve' ? 'Confirm shortlist' : 'Send feedback'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
